import type { TFunction } from 'i18next';
import type { BentoConfig, BentoFeature } from '../types';
import { ACCENT } from '../tokens';

interface Props {
  bento: BentoConfig;
  t: TFunction;
}

const ORB: Record<string, string> = {
  tr: 'absolute right-0 top-0 w-64 h-64 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2 transition-colors duration-500',
  bl: 'absolute left-0 bottom-0 w-64 h-64 rounded-full blur-3xl transform -translate-x-1/3 translate-y-1/3 transition-colors duration-500',
  br: 'absolute right-0 bottom-0 w-64 h-64 rounded-full blur-3xl transform translate-x-1/3 translate-y-1/3 transition-colors duration-500',
};

export function BentoFeatures({ bento, t }: Props) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-[minmax(250px,auto)]">
      {bento.features.map((f, i) => (
        <FeatureCard key={i} f={f} iconBorder={bento.iconBorder} t={t} />
      ))}
    </div>
  );
}

function FeatureCard({ f, iconBorder, t }: { f: BentoFeature; iconBorder?: boolean; t: TFunction }) {
  const a = ACCENT[f.accent];
  const Icon = f.icon;
  const iconBorderCls = iconBorder ? ` border ${a.border}` : '';

  if (f.size === 'full') {
    return (
      <div className="md:col-span-3 glass-card rounded-3xl p-8 sm:p-10 border border-white/[0.02] hover:border-white/10 transition-all duration-300 group">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
          <div className="w-16 h-16 shrink-0 rounded-2xl bg-dark-200 border border-white/10 flex items-center justify-center">
            <Icon className="w-8 h-8 text-text-primary" />
          </div>
          <div>
            <h3 className="text-xl sm:text-2xl font-bold text-text-primary mb-2 font-heading">{t(f.titleKey)}</h3>
            <p className="text-text-secondary text-lg leading-relaxed max-w-4xl">{t(f.descKey)}</p>
          </div>
        </div>
      </div>
    );
  }

  if (f.size === 'wide') {
    const orb = ORB[f.orbPos ?? 'tr'];
    return (
      <div className={`md:col-span-2 glass-card rounded-3xl p-8 sm:p-10 border border-white/[0.02] ${a.borderHover} transition-all duration-300 flex flex-col justify-between group overflow-hidden relative`}>
        <div className={`${orb} ${a.bgSofter}`} />
        <div className="relative z-10">
          <div className={`w-14 h-14 rounded-2xl ${a.bgSoft} flex items-center justify-center mb-6${iconBorderCls}`}>
            <Icon className={`w-7 h-7 ${a.text}`} />
          </div>
          <h3 className={`text-2xl sm:text-3xl font-bold text-text-primary mb-4 font-heading ${a.textHover} transition-colors`}>{t(f.titleKey)}</h3>
          <p className={`text-text-secondary text-base lg:text-lg leading-relaxed max-w-xl ${f.descBottomGap ? 'mb-6' : ''}`}>{t(f.descKey)}</p>
        </div>
      </div>
    );
  }

  // small
  return (
    <div className={`glass-card rounded-3xl p-8 border border-white/[0.02] ${a.borderHover} transition-all duration-300 group`}>
      <div className={`w-12 h-12 rounded-2xl ${a.bgSoft} flex items-center justify-center mb-6${iconBorderCls}`}>
        <Icon className={`w-6 h-6 ${a.text}`} />
      </div>
      <h3 className={`text-xl font-bold text-text-primary mb-3 font-heading ${a.textHover} transition-colors`}>{t(f.titleKey)}</h3>
      <p className="text-text-secondary leading-relaxed">{t(f.descKey)}</p>
    </div>
  );
}
